import { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../config/api';
import { 
  TrendingUp, Music, Check, X, Clock, Loader2, 
  RefreshCw, Filter, ChevronRight, User
} from 'lucide-react';

export default function AdminChartRequestsPage() {
  const { getAuthHeaders, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [chartTypeFilter, setChartTypeFilter] = useState('all');
  const [pendingCount, setPendingCount] = useState(0);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [denyReason, setDenyReason] = useState('');
  const [showDenyModal, setShowDenyModal] = useState(false);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter && filter !== 'all') params.append('status', filter);
      if (chartTypeFilter && chartTypeFilter !== 'all') params.append('chart_type', chartTypeFilter);
      
      const response = await fetch(`${API}/admin/chart-requests?${params}`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setRequests(data.requests);
        setPendingCount(data.pending_count);
      } else {
        toast.error('Failed to load chart requests');
      }
    } catch (err) {
      toast.error('Failed to load chart requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [filter, chartTypeFilter]); // eslint-disable-line

  const handleApprove = async (requestId) => {
    setProcessing(true);
    try {
      const response = await fetch(`${API}/admin/chart-requests/${requestId}/approve`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Request approved! Track added to chart.');
        fetchRequests();
        setSelectedRequest(null);
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to approve');
      }
    } catch (err) {
      toast.error('Failed to approve request');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeny = async () => {
    if (!selectedRequest) return;
    
    setProcessing(true);
    try {
      const response = await fetch(`${API}/admin/chart-requests/${selectedRequest.request_id}/deny`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ reason: denyReason || 'Does not meet chart criteria' })
      });
      
      if (response.ok) {
        toast.success('Request denied');
        fetchRequests();
        setSelectedRequest(null);
        setShowDenyModal(false);
        setDenyReason('');
      } else {
        toast.error('Failed to deny request');
      }
    } catch (err) {
      toast.error('Failed to deny request');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
      case 'approved':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30"><Check className="w-3 h-3 mr-1" /> Approved</Badge>;
      case 'denied':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30"><X className="w-3 h-3 mr-1" /> Denied</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const chartTypeLabels = {
    'top_20_songs': 'Top 20 Songs',
    'top_20_artists': 'Top 20 Artists',
    'rising': 'Rising',
    'new_releases': 'New Releases',
    'genre': 'Genre Chart',
    'region': 'Region Chart'
  };

  return (
    <div className="min-h-screen p-4 md:p-6" data-testid="admin-chart-requests-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-secondary" />
              Chart Requests
            </h1>
            <p className="text-muted-foreground">
              Manage track placement requests for DMV Soundstage charts
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {pendingCount > 0 && (
              <Badge className="bg-secondary/20 text-secondary">
                {pendingCount} pending
              </Badge>
            )}
            <Button variant="outline" onClick={fetchRequests} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filters:</span>
            </div>
            
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="denied">Denied</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={chartTypeFilter} onValueChange={setChartTypeFilter}>
              <SelectTrigger className="w-44" data-testid="chart-type-filter">
                <SelectValue placeholder="Chart Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Charts</SelectItem>
                <SelectItem value="top_20_songs">Top 20 Songs</SelectItem>
                <SelectItem value="top_20_artists">Top 20 Artists</SelectItem>
                <SelectItem value="rising">Rising</SelectItem>
                <SelectItem value="new_releases">New Releases</SelectItem>
                <SelectItem value="genre">Genre Charts</SelectItem>
                <SelectItem value="region">Region Charts</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Requests List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-secondary" />
          </div>
        ) : requests.length === 0 ? (
          <Card className="p-8 text-center">
            <TrendingUp className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Requests Found</h3>
            <p className="text-muted-foreground">
              {filter === 'pending' ? 'No pending chart requests at this time.' : 'No requests match your filters.'}
            </p>
          </Card>
        ) : (
          <div className="space-y-3">
            {requests.map(request => (
              <Card 
                key={request.request_id} 
                className="p-4 hover:bg-muted/30 transition-colors cursor-pointer"
                onClick={() => setSelectedRequest(request)}
                data-testid={`chart-request-${request.request_id}`}
              >
                <div className="flex items-center gap-4">
                  {/* Track Cover */}
                  <div className="w-14 h-14 rounded bg-muted overflow-hidden flex-shrink-0">
                    {request.track_details?.cover_art_url ? (
                      <img 
                        src={request.track_details.cover_art_url} 
                        alt={request.track_title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/30 to-secondary/30">
                        <Music className="w-6 h-6 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  
                  {/* Track Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold truncate">{request.track_title}</h3>
                      {getStatusBadge(request.status)}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{request.artist_name}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <Badge variant="outline" className="text-xs">
                        {chartTypeLabels[request.requested_chart] || request.requested_chart}
                      </Badge>
                      {request.genre && (
                        <span className="text-xs text-muted-foreground">{request.genre}</span>
                      )}
                      {request.region && (
                        <span className="text-xs text-muted-foreground">{request.region}</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {request.status === 'pending' && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-400 border-red-400/30 hover:bg-red-500/10"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedRequest(request);
                            setShowDenyModal(true);
                          }}
                          data-testid={`deny-${request.request_id}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleApprove(request.request_id);
                          }}
                          disabled={processing}
                          data-testid={`approve-${request.request_id}`}
                        >
                          <Check className="w-4 h-4 mr-1" /> Approve
                        </Button>
                      </>
                    )}
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <Dialog open={!!selectedRequest && !showDenyModal} onOpenChange={() => setSelectedRequest(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Chart Request Details</DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              {/* Track Info */}
              <div className="flex items-center gap-4 p-3 bg-muted/30 rounded-lg">
                <div className="w-16 h-16 rounded bg-muted overflow-hidden">
                  {selectedRequest.track_details?.cover_art_url ? (
                    <img 
                      src={selectedRequest.track_details.cover_art_url} 
                      alt={selectedRequest.track_title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/30 to-secondary/30">
                      <Music className="w-8 h-8 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div>
                  <h3 className="font-semibold">{selectedRequest.track_title}</h3>
                  <p className="text-sm text-muted-foreground">{selectedRequest.artist_name}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {selectedRequest.track_details?.plays || 0} plays
                  </p>
                </div>
              </div>
              
              {/* Request Details */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status:</span>
                  {getStatusBadge(selectedRequest.status)}
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Requested Chart:</span>
                  <span>{chartTypeLabels[selectedRequest.requested_chart]}</span>
                </div>
                {selectedRequest.genre && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Genre:</span>
                    <span>{selectedRequest.genre}</span>
                  </div>
                )}
                {selectedRequest.region && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Region:</span>
                    <span>{selectedRequest.region}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Submitted:</span>
                  <span>{new Date(selectedRequest.created_at).toLocaleDateString()}</span>
                </div>
              </div>
              
              {selectedRequest.notes && (
                <div className="p-3 bg-muted/30 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Artist Notes:</p>
                  <p className="text-sm">{selectedRequest.notes}</p>
                </div>
              )}
              
              {/* Actions */}
              {selectedRequest.status === 'pending' && (
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    className="flex-1 text-red-400 border-red-400/30"
                    onClick={() => setShowDenyModal(true)}
                  >
                    <X className="w-4 h-4 mr-2" /> Deny
                  </Button>
                  <Button
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    onClick={() => handleApprove(selectedRequest.request_id)}
                    disabled={processing}
                  >
                    {processing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    Approve
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Deny Modal */}
      <Dialog open={showDenyModal} onOpenChange={setShowDenyModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Deny Chart Request</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Provide a reason for denying this request. The artist will be notified.
            </p>
            
            <Textarea
              value={denyReason}
              onChange={(e) => setDenyReason(e.target.value)}
              placeholder="Reason for denial (optional)..."
              rows={3}
            />
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowDenyModal(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700"
                onClick={handleDeny}
                disabled={processing}
              >
                {processing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <X className="w-4 h-4 mr-2" />
                )}
                Deny Request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
